import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Loader2, Clock, Truck, Zap, TrendingDown, User, Check,
  AlertTriangle, X, Timer, MapPin, CalendarClock,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

function formatDate(str) {
  const d = new Date(str + 'T00:00:00');
  return d.toLocaleDateString('ru-RU', { weekday: 'short', day: 'numeric', month: 'short' });
}

function formatDuration(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}мин`;
  if (m === 0) return `${h}ч`;
  return `${h}ч ${m}мин`;
}

// ====== CANDIDATE CARD ======
function SlotCard({ c, index, isBestFit, isNearFit, onSelect, selecting }) {
  const isFirst = index === 0;
  return (
    <div
      className={cn(
        'p-3 rounded-lg border transition-all cursor-pointer hover:border-primary/50',
        isFirst && !isNearFit ? 'border-primary/40 bg-primary/5' : '',
        isNearFit ? 'border-amber-500/30 bg-amber-500/5' : 'border-border',
      )}
      onClick={() => onSelect(c)}
      data-testid={`schedule-card-${isNearFit ? 'nf' : isBestFit ? 'bf' : 'f'}-${index}`}
    >
      <div className="flex items-center gap-2 mb-2">
        {isFirst && !isNearFit && (
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-[10px] px-1.5">
            {isBestFit ? 'Best fit' : 'Fastest'}
          </Badge>
        )}
        {isNearFit && (
          <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 text-[10px] px-1.5">
            Near fit
          </Badge>
        )}
        {c.is_remote && <Badge variant="outline" className="text-[10px] text-orange-400 border-orange-400/30">Remote</Badge>}
        <span className="font-medium text-sm">{c.worker_name}</span>
        <div className="w-2 h-2 rounded-full ml-1" style={{ backgroundColor: c.worker_color }} />
        <span className="ml-auto text-xs text-muted-foreground">{formatDate(c.date)}</span>
      </div>

      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{c.slot_start} - {c.slot_end}</span>
        <span className="flex items-center gap-1"><Truck className="h-3 w-3" />{c.travel_before_minutes}мин + {c.travel_after_minutes}мин</span>
        <span>нагрузка {c.projected_load_percent}%</span>
        {isBestFit && !isNearFit && c.best_fit_score != null && <span className="font-mono text-primary">score {c.best_fit_score}</span>}
      </div>

      {isNearFit && (
        <div className="mt-2 p-2 rounded bg-amber-500/10 border border-amber-500/20 text-xs space-y-0.5">
          <div className="flex items-center gap-3">
            <span>Запрошено: <b>{formatDuration(c.requested_duration_minutes)}</b></span>
            <span>Доступно: <b>{formatDuration(c.available_work_minutes)}</b></span>
            <span className="text-orange-400 font-medium">Короче на {Math.round(c.duration_deficit_minutes)}мин</span>
          </div>
          <p className="text-amber-400/80 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />Требуется подтверждение менеджера
          </p>
        </div>
      )}

      {c.explanation && !isNearFit && <p className="text-[10px] text-muted-foreground mt-1 italic">{c.explanation}</p>}
      {selecting && <Loader2 className="h-3 w-3 animate-spin mt-1" />}
    </div>
  );
}

// ====== NEAR-FIT CONFIRM DIALOG ======
function NearFitConfirmDialog({ open, onOpenChange, candidate, onConfirm, loading }) {
  if (!candidate) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="order-near-fit-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-400">
            <AlertTriangle className="h-5 w-5" /> Подтверждение укороченного слота
          </DialogTitle>
          <DialogDescription>Доступный слот короче запрошенной длительности</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3 p-3 rounded-lg bg-muted/50 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Запрошено</p>
              <p className="font-semibold">{formatDuration(candidate.requested_duration_minutes)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Доступно</p>
              <p className="font-semibold text-amber-400">{formatDuration(candidate.available_work_minutes)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Работник</p>
              <p className="font-semibold">{candidate.worker_name}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Дата/время</p>
              <p className="font-semibold">{formatDate(candidate.date)} {candidate.slot_start}</p>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Отмена</Button>
          <Button onClick={onConfirm} disabled={loading}
            className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="order-near-fit-confirm-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
            Подтвердить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ====== MANUAL ASSIGN PANEL ======
function ManualPanel({ api, draftId, onSelected }) {
  const [workers, setWorkers] = useState([]);
  const [workerId, setWorkerId] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [validation, setValidation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selecting, setSelecting] = useState(false);

  useEffect(() => {
    api.get('/workers?limit=50').then(r => {
      setWorkers(Array.isArray(r.data) ? r.data.map(w => ({
        id: w.id, name: `${w.first_name || ''} ${w.last_name || ''}`.trim() || w.id,
      })) : []);
    }).catch(() => {});
  }, [api]);

  const handleValidate = async () => {
    if (!workerId || !date || !time) return toast.error('Заполните все поля');
    setLoading(true);
    try {
      const resp = await api.post(`/schedule/drafts/${draftId}/manual-validate`, {
        worker_id: workerId, date, time_start: time,
      });
      setValidation(resp.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка валидации');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async () => {
    setSelecting(true);
    try {
      const resp = await api.post(`/schedule/drafts/${draftId}/manual-select`, {
        worker_id: workerId, date, time_start: time,
      });
      toast.success('Слот заблокирован');
      onSelected(resp.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка выбора');
    } finally {
      setSelecting(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="order-manual-assign-panel">
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-xs">Работник</Label>
          <Select value={workerId} onValueChange={setWorkerId}>
            <SelectTrigger className="h-8 text-xs" data-testid="order-manual-worker"><SelectValue placeholder="Выбрать..." /></SelectTrigger>
            <SelectContent>
              {workers.map(w => <SelectItem key={w.id} value={w.id} className="text-xs">{w.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Дата</Label>
          <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="h-8 text-xs" data-testid="order-manual-date" />
        </div>
        <div>
          <Label className="text-xs">Время</Label>
          <Input type="time" value={time} onChange={e => setTime(e.target.value)} className="h-8 text-xs" data-testid="order-manual-time" />
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={handleValidate} disabled={loading}>
          {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Check className="h-3 w-3 mr-1" />}Проверить
        </Button>
        {validation && validation.status !== 'conflict' && (
          <Button size="sm" onClick={handleSelect} disabled={selecting}>
            {selecting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}Заблокировать
          </Button>
        )}
      </div>
      {validation && (
        <div className={cn(
          'p-2 rounded text-xs border',
          validation.status === 'valid' ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400' :
          validation.status === 'warning' ? 'border-amber-500/30 bg-amber-500/10 text-amber-400' :
          'border-red-500/30 bg-red-500/10 text-red-400',
        )}>
          <div className="flex items-center gap-1 font-medium mb-1">
            {validation.status === 'valid' ? <Check className="h-3 w-3" /> :
             validation.status === 'warning' ? <AlertTriangle className="h-3 w-3" /> :
             <X className="h-3 w-3" />}
            {validation.status === 'valid' ? 'ОК' : validation.status === 'warning' ? 'ПРЕДУПРЕЖДЕНИЕ' : 'КОНФЛИКТ'}
          </div>
          <p>{validation.reason}</p>
          {validation.details && (
            <div className="flex gap-3 mt-1 text-[10px] text-muted-foreground">
              <span>travel: {validation.details.travel_before_minutes}мин + {validation.details.travel_after_minutes}мин</span>
              <span>нагрузка: {validation.details.projected_load_percent}%</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ====== MAIN DIALOG ======
export function OrderScheduleDialog({ order, open, onOpenChange, onScheduled }) {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selecting, setSelecting] = useState(null);
  const [selectedLock, setSelectedLock] = useState(null);
  const [nearFitConfirmOpen, setNearFitConfirmOpen] = useState(false);
  const [nearFitCandidate, setNearFitCandidate] = useState(null);
  const [nearFitLoading, setNearFitLoading] = useState(false);

  // Fetch slots when dialog opens
  useEffect(() => {
    if (!open || !order) return;
    setData(null);
    setSelectedLock(null);
    setLoading(true);

    api.post(`/orders/${order.id}/find-slots`)
      .then(resp => {
        setData(resp.data);
        if (resp.data.geocoding_warning) {
          toast.warning(resp.data.geocoding_warning);
        }
      })
      .catch(err => {
        toast.error(err.response?.data?.detail || 'Ошибка поиска слотов');
      })
      .finally(() => setLoading(false));
  }, [open, order, api]);

  const handleSelectCandidate = async (candidate, mode) => {
    if (!data) return;

    if (candidate.fit_type === 'near') {
      setNearFitCandidate(candidate);
      setNearFitConfirmOpen(true);
      return;
    }

    setSelecting(candidate.slot_start + candidate.date);
    try {
      const resp = await api.post(`/schedule/drafts/${data.draft_id}/select-slot`, {
        mode, candidate,
      });
      setSelectedLock(resp.data);
      toast.success(`Слот заблокирован: ${candidate.worker_name} ${formatDate(candidate.date)} ${candidate.slot_start}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Слот недоступен');
    } finally {
      setSelecting(null);
    }
  };

  const handleNearFitConfirm = async () => {
    if (!data || !nearFitCandidate) return;
    setNearFitLoading(true);
    try {
      const resp = await api.post(`/schedule/drafts/${data.draft_id}/select-slot`, {
        mode: 'near_fit',
        candidate: nearFitCandidate,
        manager_confirmed_shorter_slot: true,
      });
      setSelectedLock(resp.data);
      toast.success(`Near-fit слот заблокирован: ${nearFitCandidate.worker_name}`);
      setNearFitConfirmOpen(false);
      setNearFitCandidate(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Слот недоступен');
    } finally {
      setNearFitLoading(false);
    }
  };

  const handleManualSelected = (lockData) => {
    setSelectedLock({ lock_expires_at: new Date(Date.now() + 3600000).toISOString(), ...lockData });
  };

  const handleCompleteSchedule = async () => {
    if (!selectedLock || !data || !order) return;
    try {
      // Update order with the scheduled data
      await api.patch(`/orders/${order.id}`, {
        scheduled_date: selectedLock.date,
        scheduled_time_start: selectedLock.time_start || selectedLock.slot_start,
        scheduled_time_end: selectedLock.time_end || selectedLock.slot_end,
        operational_status: undefined,
      });

      // Assign the worker to the order
      const workerId = selectedLock.worker_id;
      if (workerId) {
        try {
          await api.post(`/orders/${order.id}/assignments`, null, {
            params: { worker_id: workerId },
          });
        } catch {
          // Worker might already be assigned
        }
      }

      // Change status to confirmed
      await api.post(`/orders/${order.id}/actions/change-operational-status`, {
        new_status: 'confirmed',
      });

      toast.success('Заказ запланирован!');
      onScheduled?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка при завершении планирования');
    }
  };

  const handleDiscard = async () => {
    if (!data) return;
    try {
      await api.delete(`/schedule/drafts/${data.draft_id}`);
      setData(null);
      setSelectedLock(null);
      onOpenChange(false);
    } catch {
      // Silent
    }
  };

  const nearFitResults = data?.near_fit_results || [];
  const hasFullFit = data?.has_full_fit;
  const hasNearFit = data?.has_near_fit;
  const tabCount = 3 + (nearFitResults.length > 0 ? 1 : 0);

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => {
        if (!v && data && !selectedLock) handleDiscard();
        onOpenChange(v);
      }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="order-schedule-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-primary" />
              Планирование: {order?.title}
            </DialogTitle>
            <DialogDescription>
              {order?.address_street && (
                <span className="flex items-center gap-1 text-muted-foreground">
                  <MapPin className="h-3 w-3" />{order.address_street}
                </span>
              )}
              {data && (
                <span className="ml-2">
                  Длительность: {formatDuration(data.duration_minutes)}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {/* Loading state */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-3 text-muted-foreground">Поиск доступных слотов...</span>
            </div>
          )}

          {/* Remote warning */}
          {data?.is_remote && (
            <div className="p-3 rounded-lg border border-orange-500/30 bg-orange-500/10 text-sm" data-testid="remote-order-warning">
              <div className="flex items-center gap-2 font-medium text-orange-400 mb-1">
                <AlertTriangle className="h-4 w-4" />
                Удалённый заказ (более 50 км)
              </div>
              <p className="text-muted-foreground text-xs">
                Этот заказ находится далеко от базы. Рекомендуется дождаться кластера заказов
                в том же районе или добавить наценку за топливо.
              </p>
              <div className="flex gap-2 mt-2">
                <Badge variant="outline" className="text-orange-400 border-orange-400/30 text-xs">
                  Можно запланировать с наценкой
                </Badge>
                <Badge variant="outline" className="text-blue-400 border-blue-400/30 text-xs">
                  Или ожидать кластер
                </Badge>
              </div>
            </div>
          )}

          {/* Selected lock banner */}
          {selectedLock && (
            <div className="p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10" data-testid="slot-locked-banner">
              <div className="flex items-center gap-2 text-emerald-400 font-medium text-sm mb-1">
                <Check className="h-4 w-4" />
                Слот заблокирован
              </div>
              <p className="text-xs text-muted-foreground">
                {selectedLock.worker_id && `Работник: ${selectedLock.worker_id}`}
                {selectedLock.date && ` | ${formatDate(selectedLock.date)}`}
                {selectedLock.time_start && ` ${selectedLock.time_start}-${selectedLock.time_end}`}
              </p>
            </div>
          )}

          {/* No full-fit banner */}
          {data && !hasFullFit && hasNearFit && (
            <div className="p-2 rounded text-xs border border-amber-500/30 bg-amber-500/10 text-amber-400 flex items-center gap-2">
              <Timer className="h-3.5 w-3.5 flex-shrink-0" />
              <span>Нет полного слота на {formatDuration(data.duration_minutes)}. Найдены короткие альтернативы (Near Fit).</span>
            </div>
          )}

          {/* Results tabs */}
          {data && !loading && (
            <Tabs defaultValue={hasFullFit ? "fastest" : (hasNearFit ? "nearfit" : "fastest")} className="mt-2">
              <TabsList className={cn("grid w-full h-9", tabCount === 4 ? "grid-cols-4" : "grid-cols-3")}>
                <TabsTrigger value="fastest" className="text-xs" data-testid="order-tab-fastest">
                  <Zap className="h-3 w-3 mr-1" />Быстрый ({data.fastest_results?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="bestfit" className="text-xs" data-testid="order-tab-bestfit">
                  <TrendingDown className="h-3 w-3 mr-1" />Best Fit ({data.best_fit_results?.length || 0})
                </TabsTrigger>
                {nearFitResults.length > 0 && (
                  <TabsTrigger value="nearfit" className="text-xs" data-testid="order-tab-nearfit">
                    <Timer className="h-3 w-3 mr-1" />Near Fit ({nearFitResults.length})
                  </TabsTrigger>
                )}
                <TabsTrigger value="manual" className="text-xs" data-testid="order-tab-manual">
                  <User className="h-3 w-3 mr-1" />Ручной
                </TabsTrigger>
              </TabsList>

              <TabsContent value="fastest" className="mt-2 space-y-2">
                {data.fastest_results?.map((c, i) => (
                  <SlotCard key={i} c={c} index={i} isBestFit={false} isNearFit={false}
                    onSelect={c => handleSelectCandidate(c, 'fastest')} selecting={selecting === c.slot_start + c.date} />
                ))}
                {(!data.fastest_results || data.fastest_results.length === 0) && (
                  <p className="text-xs text-muted-foreground text-center py-6">
                    {hasNearFit ? 'Нет полных слотов. Проверьте вкладку Near Fit.' : 'Нет доступных слотов'}
                  </p>
                )}
              </TabsContent>

              <TabsContent value="bestfit" className="mt-2 space-y-2">
                {data.best_fit_results?.map((c, i) => (
                  <SlotCard key={i} c={c} index={i} isBestFit={true} isNearFit={false}
                    onSelect={c => handleSelectCandidate(c, 'best_fit')} selecting={selecting === c.slot_start + c.date} />
                ))}
                {(!data.best_fit_results || data.best_fit_results.length === 0) && (
                  <p className="text-xs text-muted-foreground text-center py-6">
                    {hasNearFit ? 'Нет полных слотов. Проверьте вкладку Near Fit.' : 'Нет доступных слотов'}
                  </p>
                )}
              </TabsContent>

              {nearFitResults.length > 0 && (
                <TabsContent value="nearfit" className="mt-2 space-y-2">
                  <div className="p-2 rounded text-xs border border-amber-500/20 bg-amber-500/5 text-amber-400/80 mb-2">
                    Эти слоты короче запрошенного ({formatDuration(data.duration_minutes)}). Выбор требует подтверждения.
                  </div>
                  {nearFitResults.map((c, i) => (
                    <SlotCard key={i} c={c} index={i} isBestFit={false} isNearFit={true}
                      onSelect={c => handleSelectCandidate(c, 'near_fit')} selecting={selecting === c.slot_start + c.date} />
                  ))}
                </TabsContent>
              )}

              <TabsContent value="manual" className="mt-2">
                {data.draft_id && (
                  <ManualPanel api={api} draftId={data.draft_id} onSelected={handleManualSelected} />
                )}
              </TabsContent>
            </Tabs>
          )}

          {/* Footer actions */}
          {data && !loading && (
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => { handleDiscard(); }} data-testid="schedule-cancel-btn">
                Отмена
              </Button>
              {selectedLock && (
                <Button onClick={handleCompleteSchedule} data-testid="schedule-confirm-btn"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Check className="h-4 w-4 mr-2" />
                  Запланировать заказ
                </Button>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <NearFitConfirmDialog
        open={nearFitConfirmOpen}
        onOpenChange={setNearFitConfirmOpen}
        candidate={nearFitCandidate}
        onConfirm={handleNearFitConfirm}
        loading={nearFitLoading}
      />
    </>
  );
}
